import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Autocomplete from '@mui/material/Autocomplete';
import * as accountService from '../../services/accountService';
import Chip from '@mui/material/Chip';
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Paper,
  alpha,
  FormHelperText
} from '@mui/material';
import { Contact, ContactFormData } from '../../types/contact';
import { contactsApi, branchesApi } from '../../services/api';
import { Branch } from '../../types/branch';
import apiService from '../../services/apiService';

const validationSchema = Yup.object().shape({
  type: Yup.string().required('النوع مطلوب'),
  name: Yup.string().required('الاسم مطلوب'),
  email: Yup.string().email('بريد إلكتروني غير صالح').optional(),
  phone: Yup.string(),
  category: Yup.string().required('الفئة مطلوبة'),
  status: Yup.string().required('الحالة مطلوبة'),
  taxNumber: Yup.string(),
  branches: Yup.array().of(Yup.string()),
  notes: Yup.string().optional(),
  'address.street': Yup.string(),
  'address.city': Yup.string(),
  'address.country': Yup.string(),
  'address.state': Yup.string(),
  'address.postalCode': Yup.string(),
  // التحقق من صحة بيانات الحساب المرتبط
  linkedAccount: Yup.boolean(),
  accountId: Yup.string().nullable().when(['linkedAccount', 'autoCreateAccount'], {
    is: (linkedAccount: boolean, autoCreateAccount: boolean) => linkedAccount === true && autoCreateAccount === false,
    then: (schema) => schema.required('يجب اختيار حساب عند تفعيل الحساب المرتبط واختيار الربط اليدوي'),
    otherwise: (schema) => schema.nullable().optional()
  }),
  autoCreateAccount: Yup.boolean(),
});

const initialValues: ContactFormData = {
  type: 'customer',
  name: '',
  email: '',
  phone: '',
  address: {
    street: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  },
  category: 'regular',
  status: 'active',
  taxNumber: '',
  branches: [],
  notes: '',
  customFields: {},
  // قيم افتراضية للحساب المرتبط
  linkedAccount: false,
  autoCreateAccount: true,
  accountId: '',
  // إضافة حقل مؤقت لعرض رقم الحساب المقترح
  suggestedAccountNumber: ''
};

const ContactForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [error, setError] = useState<string>('');
  const [contact, setContact] = useState<Contact | null>(null);
  const [availableBranches, setAvailableBranches] = useState<Branch[]>([]);
  const [availableAccounts, setAvailableAccounts] = useState<Array<{id: string, name: string, accountNumber: string, type?: string}>>([]);
  const [suggestedAccountNumber, setSuggestedAccountNumber] = useState<string>('');
  
  // حالة لتخزين الحسابات الرئيسية للعملاء والموردين
  const [defaultAccounts, setDefaultAccounts] = useState<{
    customerAccount: { id: string, name: string, accountNumber: string, type?: string } | null,
    supplierAccount: { id: string, name: string, accountNumber: string, type?: string } | null
  }>({
    customerAccount: null,
    supplierAccount: null
  });

  // جلب الحساب الفعلي من قائمة الحسابات باستخدام الإعدادات
  const getAccountById = (accountId: string, accounts: Array<{id: string, name: string, accountNumber: string, type?: string}>) => {
    return accounts.find(acc => acc.id === accountId) || null;
  };

  // تحديث الحسابات الرئيسية
  const updateDefaultAccounts = (
    settings: {
      customerDefaultAccount: string | null,
      supplierDefaultAccount: string | null
    }, 
    accounts: Array<{id: string, name: string, accountNumber: string, type?: string}>
  ) => {
    // جلب الحسابات الفعلية من قائمة الحسابات باستخدام معرفات الإعدادات
    const customerAccount = settings.customerDefaultAccount 
      ? getAccountById(settings.customerDefaultAccount, accounts) 
      : null;
    
    const supplierAccount = settings.supplierDefaultAccount 
      ? getAccountById(settings.supplierDefaultAccount, accounts) 
      : null;
    
    setDefaultAccounts({
      customerAccount,
      supplierAccount
    });
    
    console.log('تم تحديد الحسابات الرئيسية من الإعدادات:', { 
      customerAccount, 
      supplierAccount 
    });
  };

  // جلب الحسابات الفرعية لحساب معين لتوليد رقم حساب فريد
  const getNextAccountNumber = async (parentAccountId: string): Promise<string> => {
    try {
      // تحديد الحساب الرئيسي
      const parentAccount = availableAccounts.find(acc => acc.id === parentAccountId);
      
      if (!parentAccount || !parentAccount.accountNumber) {
        console.error('لم يتم العثور على الحساب الرئيسي المحدد');
        return '';
      }
      
      // جلب الحسابات الفرعية للحساب الرئيسي المحدد
      // استخدام نفس الطريقة المستخدمة في AccountForm
      const subAccounts = await apiService.get<Array<{id: string, accountNumber: string, name: string}>>(`/finance/accounts?parentAccount=${parentAccountId}&type=فرعي`);
      
      let maxSequence = 0;
      const parentNumber = parentAccount.accountNumber;
      
      // البحث عن أعلى رقم تسلسلي في الحسابات الفرعية الحالية
      if (Array.isArray(subAccounts) && subAccounts.length > 0) {
        subAccounts.forEach(account => {
          if (account.accountNumber.startsWith(parentNumber)) {
            // استخراج الرقم التسلسلي (الأرقام بعد رقم الحساب الرئيسي)
            const sequencePart = account.accountNumber.substring(parentNumber.length);
            const sequenceNumber = parseInt(sequencePart, 10);
            
            if (!isNaN(sequenceNumber) && sequenceNumber > maxSequence) {
              maxSequence = sequenceNumber;
            }
          }
        });
      }
      
      // زيادة الرقم التسلسلي بواحد للحساب الفرعي الجديد
      const nextSequence = maxSequence + 1;
      
      // تنسيق الرقم التسلسلي بإضافة أصفار قبله
      const formattedSequence = nextSequence.toString().padStart(2, '0');
      
      // إنشاء رقم الحساب الفرعي الجديد
      const suggestedNumber = `${parentNumber}${formattedSequence}`;
      
      console.log(`توليد رقم حساب فرعي تسلسلي: ${suggestedNumber} (الأب: ${parentNumber}, التسلسل: ${formattedSequence}, ناتج من أعلى تسلسل: ${maxSequence})`);
      
      return suggestedNumber;
    } catch (error) {
      console.error('خطأ في توليد رقم الحساب التسلسلي:', error);
      
      // في حال فشل الطلب، نقوم بتوليد رقم افتراضي
      const parentAccount = availableAccounts.find(acc => acc.id === parentAccountId);
      if (parentAccount && parentAccount.accountNumber) {
        return `${parentAccount.accountNumber}01`;
      }
      return '';
    }
  };

  // جلب قائمة الحسابات النشطة
  const fetchAccounts = async () => {
    try {
      const accounts = await apiService.get<Array<{id: string, name: string, accountNumber: string, type: string}>>('/finance/accounts/selection/active');
      setAvailableAccounts(accounts);
      
      // محاولة جلب إعدادات الحسابات المرتبطة
      try {
        const linkedSettings = await apiService.get<{
          customerDefaultAccount: string | null,
          supplierDefaultAccount: string | null
        }>('/settings/linked-accounts');
        
        console.log('تم جلب إعدادات الحسابات المرتبطة:', linkedSettings);
        
        // تحديث الحسابات الافتراضية باستخدام الإعدادات والحسابات المتاحة
        updateDefaultAccounts(linkedSettings, accounts);
      } catch (settingsError) {
        console.error('خطأ في جلب إعدادات الحسابات المرتبطة:', settingsError);
        
        // محاولة العثور على الحسابات الافتراضية بناءً على الأسماء كخطة بديلة
        const customerAccount = accounts.find(acc => 
          acc.name.includes('عملاء') || 
          acc.name.toLowerCase().includes('customer')
        );
        
        const supplierAccount = accounts.find(acc => 
          acc.name.includes('موردين') || 
          acc.name.toLowerCase().includes('supplier')
        );
        
        if (customerAccount || supplierAccount) {
          setDefaultAccounts({
            customerAccount: customerAccount || null,
            supplierAccount: supplierAccount || null
          });
          
          console.log('تم تحديد الحسابات الرئيسية من الأسماء:', { 
            customerAccount, 
            supplierAccount 
          });
        }
      }
    } catch (error) {
      console.error('خطأ في جلب قائمة الحسابات:', error);
      setError('حدث خطأ أثناء جلب قائمة الحسابات');
    }
  };

  useEffect(() => {
    if (id) {
      fetchContact();
    }
    fetchBranches();
    fetchAccounts(); // سيقوم بجلب الحسابات وإعدادات الحسابات المرتبطة معًا
  }, [id]);

  const fetchContact = async () => {
    try {
      const data = await contactsApi.getContact(id!);
      setContact(data);
    } catch (error) {
      setError('حدث خطأ أثناء تحميل بيانات الاتصال');
    }
  };

  // جلب قائمة الفروع المتاحة
  const fetchBranches = async () => {
    try {
      const response = await branchesApi.getBranches({ status: 'active' });
      setAvailableBranches(response.branches);
    } catch (error) {
      console.error('خطأ في جلب قائمة الفروع:', error);
      setError('حدث خطأ أثناء جلب قائمة الفروع');
    }
  };

  // تحديث رقم الحساب المقترح
  const updateSuggestedAccountNumber = async (values: ContactFormData, setFieldValue: (field: string, value: any) => void) => {
    if (values.linkedAccount && values.autoCreateAccount) {
      const contactType = values.type;
      const defaultAccount = contactType === 'customer' 
        ? defaultAccounts.customerAccount 
        : defaultAccounts.supplierAccount;
      
      if (defaultAccount && defaultAccount.id) {
        try {
          const newAccountNumber = await getNextAccountNumber(defaultAccount.id);
          if (newAccountNumber) {
            setSuggestedAccountNumber(newAccountNumber);
            setFieldValue('suggestedAccountNumber', newAccountNumber);
          }
        } catch (error) {
          console.error('خطأ في توليد رقم الحساب:', error);
          // استخدام رقم افتراضي
          if (defaultAccount.accountNumber) {
            const defaultNumber = `${defaultAccount.accountNumber}01`;
            setSuggestedAccountNumber(defaultNumber);
            setFieldValue('suggestedAccountNumber', defaultNumber);
          }
        }
      } else {
        console.error(`لم يتم العثور على حساب رئيسي افتراضي للـ ${contactType === 'customer' ? 'عملاء' : 'موردين'}`);
        
        // توليد رقم افتراضي في حالة عدم وجود حساب
        const defaultNumber = contactType === 'customer' ? 'C0001' : 'S0001';
        setSuggestedAccountNumber(defaultNumber);
        setFieldValue('suggestedAccountNumber', defaultNumber);
        
        // عرض تنبيه للمستخدم بدلاً من رسالة خطأ
        setError(`ملاحظة: لم يتم العثور على حساب رئيسي للـ ${contactType === 'customer' ? 'عملاء' : 'موردين'} في النظام. سيتم استخدام رقم حساب افتراضي (${defaultNumber}).`);
      }
    } else {
      setSuggestedAccountNumber('');
      setFieldValue('suggestedAccountNumber', '');
    }
  };

  const handleSubmit = async (values: ContactFormData) => {
    try {
      // Handle empty email - remove it completely if empty so backend won't process it
      const contactData = { ...values };
      if (!contactData.email || contactData.email.trim() === '') {
        delete contactData.email;
      }
      
      // تحويل notes من نص إلى مصفوفة من الكائنات
      if (contactData.notes && typeof contactData.notes === 'string') {
        const notesText = contactData.notes.trim();
        if (notesText) {
          contactData.notes = [{
            content: notesText,
            createdAt: new Date().toISOString()
          }] as any;
        } else {
          contactData.notes = [] as any;
        }
      }
      
      // في حالة التحديث، تحقق مما إذا كان الحساب المرتبط قد تم إلغاء تفعيله
      if (id && contact) {
        // إذا كان لدى جهة الاتصال حساب مرتبط سابقًا وتم إلغاء تفعيله الآن
        if (contact.accountId && contact.linkedAccount === true && contactData.linkedAccount === false) {
          console.log(`جهة الاتصال لديها حساب مرتبط (${contact.accountId}) وتم إلغاء تفعيله الآن، سيتم تعطيل الحساب...`);
          
          try {
            // تحديث حالة الحساب إلى غير نشط
            // معالجة حالة accountId قد يكون كائنًا أو سلسلة نصية
            const accountId = typeof contact.accountId === 'object' && contact.accountId !== null
              ? (contact.accountId as any)._id || contact.accountId.toString()
              : contact.accountId as string;
              
            await accountService.updateAccountStatus(accountId, false);
            console.log(`✅ تم تحويل الحساب ${accountId} إلى غير نشط بنجاح!`);
          } catch (accountError: any) {
            console.error('خطأ في تحديث حالة الحساب:', accountError);
            // لا نريد أن نفشل العملية كاملة بسبب خطأ في تحديث الحساب
          }
        }
        
        await contactsApi.updateContact(id, contactData);
      } else {
        await contactsApi.createContact(contactData);
      }
      navigate('/contacts');
    } catch (error: any) {
      console.error('Error submitting contact:', error);
      setError(error.message || 'حدث خطأ أثناء حفظ البيانات');
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        {id ? 'تعديل بيانات الاتصال' : 'إضافة اتصال جديد'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Formik
        initialValues={contact ? {
          ...contact,
          notes: contact.notes?.map(note => note.content).join('\n') || '',
          accountId: typeof contact.accountId === 'object' && contact.accountId !== null
            ? (contact.accountId as any)._id
            : contact.accountId,
          _id: undefined,
          createdAt: undefined,
          updatedAt: undefined
        } : initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, handleChange, values, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="type"
                  label="النوع"
                  value={values.type}
                  onChange={(e) => {
                    handleChange(e);
                    // تحديث الحساب المقترح بعد تغيير النوع
                    setTimeout(() => updateSuggestedAccountNumber(
                      { ...values, type: e.target.value as 'customer' | 'supplier' },
                      setFieldValue
                    ), 0);
                  }}
                  error={touched.type && Boolean(errors.type)}
                  helperText={touched.type && errors.type}
                  fullWidth
                >
                  <MenuItem value="customer">عميل</MenuItem>
                  <MenuItem value="supplier">مورد</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="الاسم"
                  value={values.name}
                  onChange={(e) => {
                    handleChange(e);
                    // تحديث الحساب المقترح بعد تغيير الاسم
                    setTimeout(() => updateSuggestedAccountNumber(
                      { ...values, name: e.target.value },
                      setFieldValue
                    ), 0);
                  }}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="البريد الإلكتروني"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label="رقم الهاتف"
                  value={values.phone}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="taxNumber"
                  label="الرقم الضريبي"
                  value={values.taxNumber || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="category"
                  label="الفئة"
                  value={values.category}
                  onChange={handleChange}
                  error={touched.category && Boolean(errors.category)}
                  helperText={touched.category && errors.category}
                  fullWidth
                >
                  <MenuItem value="regular">عادي</MenuItem>
                  <MenuItem value="premium">مميز</MenuItem>
                  <MenuItem value="vip">VIP</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="status"
                  label="الحالة"
                  value={values.status}
                  onChange={handleChange}
                  error={touched.status && Boolean(errors.status)}
                  helperText={touched.status && errors.status}
                  fullWidth
                >
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="branches"
                  options={availableBranches}
                  getOptionLabel={(option) => typeof option === 'string' 
                    ? availableBranches.find(b => b._id === option)?.name || option 
                    : option.name
                  }
                  value={availableBranches.filter(branch => 
                    values.branches?.includes(branch._id)
                  )}
                  onChange={(event, newValue) => {
                    const branchIds = newValue.map(branch => branch._id);
                    setFieldValue('branches', branchIds);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الفروع"
                      placeholder="اختر فروع العميل/المورد"
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  العنوان
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="address.street"
                  label="الشارع"
                  value={values.address?.street || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="address.city"
                  label="المدينة"
                  value={values.address?.city || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="address.state"
                  label="المنطقة"
                  value={values.address?.state || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="address.country"
                  label="الدولة"
                  value={values.address?.country || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="address.postalCode"
                  label="الرمز البريدي"
                  value={values.address?.postalCode || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  إعدادات الحساب المرتبط
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: alpha('#000', 0.08),
                    borderRadius: 1
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.linkedAccount}
                            onChange={(e) => {
                              setFieldValue('linkedAccount', e.target.checked);
                              if (!e.target.checked) {
                                setFieldValue('accountId', '');
                                setFieldValue('suggestedAccountNumber', '');
                                setSuggestedAccountNumber('');
                              } else {
                                // تحديث رقم الحساب المقترح عند تفعيل الحساب المرتبط
                                setTimeout(() => updateSuggestedAccountNumber(
                                  { ...values, linkedAccount: e.target.checked },
                                  setFieldValue
                                ), 0);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="تفعيل الحساب المرتبط"
                      />
                      <FormHelperText>
                        عند تفعيل هذا الخيار، سيتم ربط {values.type === 'customer' ? 'العميل' : 'المورد'} بحساب محاسبي في دفتر الأستاذ
                      </FormHelperText>
                    </Grid>

                    {values.linkedAccount && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.autoCreateAccount}
                                onChange={(e) => {
                                  setFieldValue('autoCreateAccount', e.target.checked);
                                  if (e.target.checked) {
                                    setFieldValue('accountId', '');
                                    // تحديث رقم الحساب المقترح عند تفعيل الإنشاء التلقائي
                                    setTimeout(() => updateSuggestedAccountNumber(
                                      { ...values, autoCreateAccount: e.target.checked },
                                      setFieldValue
                                    ), 0);
                                  } else {
                                    setFieldValue('suggestedAccountNumber', '');
                                    setSuggestedAccountNumber('');
                                  }
                                }}
                                color="primary"
                              />
                            }
                            label="إنشاء حساب تلقائي"
                          />
                          <FormHelperText>
                            سيتم إنشاء حساب تلقائياً تحت الحساب الرئيسي {values.type === 'customer' ? 'للعملاء' : 'للموردين'} المحدد في إعدادات النظام
                          </FormHelperText>
                        </Grid>
                        
                        {/* عرض رقم الحساب المقترح عند تفعيل الإنشاء التلقائي */}
                        {values.linkedAccount && values.autoCreateAccount && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ 
                              border: '1px dashed',
                              borderColor: 'primary.main',
                              borderRadius: 1,
                              p: 2,
                              bgcolor: alpha('#2196f3', 0.05)
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                رقم الحساب المقترح:
                              </Typography>
                              <Box sx={{ 
                                bgcolor: 'white',
                                p: 1.5,
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '1.1rem',
                                letterSpacing: 1,
                                fontWeight: 'bold',
                                color: 'success.main',
                                textAlign: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                {suggestedAccountNumber || 
                                  (values.type === 'customer' 
                                    ? (defaultAccounts.customerAccount?.accountNumber + '01' || 'C0001') 
                                    : (defaultAccounts.supplierAccount?.accountNumber + '01' || 'S0001'))
                                }
                              </Box>
                              <FormHelperText sx={{ mt: 1, fontSize: '0.8rem' }}>
                                سيتم استخدام هذا الرقم عند إنشاء الحساب بعد حفظ {values.type === 'customer' ? 'العميل' : 'المورد'}
                              </FormHelperText>
                              <FormHelperText sx={{ mt: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                                يتم توليد الرقم بناءً على نظام الترقيم المتسلسل للحسابات الفرعية تحت الحساب الرئيسي المحدد في إعدادات النظام
                              </FormHelperText>
                              {!defaultAccounts.customerAccount && values.type === 'customer' && (
                                <Alert severity="info" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                  لم يتم العثور على حساب رئيسي للعملاء. سيتم استخدام رقم افتراضي.
                                </Alert>
                              )}
                              {!defaultAccounts.supplierAccount && values.type === 'supplier' && (
                                <Alert severity="info" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                  لم يتم العثور على حساب رئيسي للموردين. سيتم استخدام رقم افتراضي.
                                </Alert>
                              )}
                            </Box>
                          </Grid>
                        )}

                        {!values.autoCreateAccount && (
                          <Grid item xs={12} sm={6}>
                            <FormControl
                              fullWidth
                              error={touched.accountId && Boolean(errors.accountId)}
                            >
                              <InputLabel>اختر الحساب</InputLabel>
                              <Select
                                name="accountId"
                                value={values.accountId || ''}
                                onChange={handleChange}
                                label="اختر الحساب"
                              >
                                <MenuItem value="">
                                  <em>اختر الحساب</em>
                                </MenuItem>
                                {availableAccounts.map((account) => (
                                  <MenuItem key={account.id} value={account.id}>
                                    {account.accountNumber} - {account.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {touched.accountId && errors.accountId && (
                                <FormHelperText>{errors.accountId as string}</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="ملاحظات"
                  multiline
                  rows={4}
                  value={values.notes || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ ml: 1 }}
                >
                  {id ? 'تحديث' : 'إضافة'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/contacts')}
                >
                  إلغاء
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ContactForm;